import { getLink } from '@models/Item';
import getTextColor from 'aquilla/core/shared/utils/getTextColor';
import { sessionStates } from '@constants/index';
import { nFormatter } from '@utils/textHelper';
import { getLinkPath } from '@models/Course';
import pushRoute from '@utils/pushRoute';
import Image from '@base/FutureImage';
import IconButton from 'aquilla/core/molecules/IconButton';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import styled from '@emotion/styled';
import {
  compareDates,
  getDateMonthString,
  getTimeDifference,
  getHoursMinutesValueFromSeconds
} from '@utils/dates';
import { mediaQuery, Spacings } from '@styles/index';
import Tag from 'aquilla/core/atoms/Tag';
import Play from 'aquilla/icons/actions/Play';
import segmentStorage from '@events/segmentStorage';

const ClassThumbnailWrapper = styled.div`
  position: relative;
  width: 272px;
`;

const ClassThumbnail = styled.div`
  position: relative;
  width: 272px;
`;

const PreviewContainer = styled.div`
  ${mediaQuery.uptoMobile} {
    position: relative;
  }
`;

const PreviewWrapper = styled.div`
  position: relative;
  display: inline-flex;
  cursor: pointer;
`;

const PlayIconButton = styled(IconButton)`
  border-radius: 50%;
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_4B};
  border: none;
  width: 56px;
  height: 56px;
  padding: ${(props) => props.theme.measurements.spacing(7)};
  background: ${({ $buttonColor, $isLive }) =>
    $isLive ? 'var(--color-i-red)' : `${$buttonColor}33`};
  transition: 0.5s;

  &:hover,
  &:active,
  &:focus {
    background: ${({ $buttonColor, $isLive }) =>
      $isLive ? 'var(--color-i-red)' : `${$buttonColor}33`};
    transform: scale(1.1);
  }
  ${mediaQuery.uptoMobile} {
    width: 40px;
    height: 40px;
  }
  svg {
    color: ${({ $buttonColor, $isLive }) =>
      $isLive ? 'var(--color-i-white)' : `${$buttonColor}`};
  }
`;

const ImageWrapper = styled.div`
  width: 464px;
  ${mediaQuery.uptoTablet} {
    width: 400px;
  }
  ${mediaQuery.uptoMobile} {
    width: calc(100vw - 32px);
  }

  img {
    aspect-ratio: 16/9;
    height: unset;
    ${mediaQuery.uptoMobile} {
      width: 100%;
    }
  }
`;

const EduImage = styled(Image)`
  object-fit: cover;
  border-radius: 8px;
`;

const LiveClassTag = styled(Tag)`
  text-transform: uppercase;
  position: absolute;
  left: ${Spacings.SPACING_4B};
  bottom: ${Spacings.SPACING_4B};
  padding: ${(props) => props.theme.measurements.spacing(2)}
    ${(props) => props.theme.measurements.spacing(3)};
`;

export function getClassCard(coverPhoto, color, width, height) {
  // eslint-disable-next-line func-names, react/display-name
  return function () {
    if (!coverPhoto)
      return (
        <EducatorThumbnailBackground
          variant="content-thumbnail-with-person"
          color={color}
          width={width || 272}
          height={height || 156}
        />
      );
    return (
      <ClassThumbnailWrapper>
        <Image
          src={coverPhoto}
          alt=""
          height={height || 156}
          width={width || 272}
        />
      </ClassThumbnailWrapper>
    );
  };
}

const classStatesMap = {
  [sessionStates.FUTURE]: 'Upcoming',
  [sessionStates.LIVE]: 'Live',
  [sessionStates.PAST]: 'Completed',
  [sessionStates.DORMANT]: 'Dormont'
};

export const getClassDetailsLine = (classInfo) => {
  const { startsAt, endsAt } = classInfo;
  if (!startsAt) return null;
  const startsAtDate = new Date(startsAt);
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const startsOn = getDateMonthString(startsAtDate);
  const classDuration = getHoursMinutesValueFromSeconds(
    getTimeDifference(startsAtDate, endsAtDate) * 60
  );
  let dateString = '';
  dateString += `${startsOn} • `;
  dateString += classDuration;

  return dateString;
};
export function formatClassDetail({
  classInfo,
  hideViewCount = false,
  hideDetailsLine = true,
  onClick
}) {
  const {
    author,
    name,
    topicGroups,
    languageDisplay,
    coverPhotoV1,
    nextSession,
    totalViews
  } = classInfo;
  const variant = classStatesMap[nextSession?.properties?.state];
  const viewCount = hideViewCount
    ? null
    : totalViews || nextSession?.properties?.attendance || 0;

  const handleOnClick = () => {
    if (onClick) onClick(classInfo);
    const route = {
      href: getLinkPath(classInfo),
      as: getLink(classInfo)
    };
    pushRoute(route.href, route.as);
  };

  const classDetail = {
    educatorName: `${author.firstName} ${author.lastName}`,
    educatorLink: `/@${author.username}`,
    title: name,
    topicName: topicGroups[0]?.name,
    topicColor: topicGroups[0]?.name
      ? getTextColor(topicGroups[0]?.name[0] || '')
      : '#8873FF',
    langLabel: languageDisplay,
    ...(variant && { variant }),
    thumbnail: getClassCard(
      coverPhotoV1,
      getTextColor(topicGroups[0]?.name[0] || '')
    ),
    ...(viewCount && { viewCount: nFormatter(viewCount, 1) }),
    onClick: handleOnClick,
    detailsLine: hideDetailsLine ? '' : getClassDetailsLine(classInfo)
  };

  return classDetail;
}

// thumbnail for educator
export function getCourseThumbnail(coverPhoto) {
  // eslint-disable-next-line func-names, react/display-name
  return function () {
    return (
      <ClassThumbnail>
        <Image src={coverPhoto} alt="" height={156} width={272} />
      </ClassThumbnail>
    );
  };
}

export function getLabel(startsAt, endsAt) {
  const today = new Date();
  const startDateRef = compareDates(today, startsAt);
  const endDateRef = compareDates(today, endsAt);
  let label = '';

  if (startDateRef > 0) label = `Starts on ${getDateMonthString(startsAt)}`;
  if (startDateRef === 0) label = 'Starts today';
  if (startDateRef < 0) label = 'In progress';
  if (endDateRef < 0) label = `Ended on ${getDateMonthString(endsAt)}`;
  return label;
}

export function formatCourse(course, lpss) {
  const {
    name,
    languageDisplay,
    topicGroups,
    author,
    coverPhoto,
    coverPhotoV1,
    startsAt,
    endsAt
  } = course;
  const label = getLabel(startsAt, endsAt);
  const handleOnClick = () => {
    // Adding LPSS to segment from label property and deleting it on class exit
    // in case property is not deleted removing it if label is not passed
    if (lpss) segmentStorage.setItem('lpss', lpss);
    else segmentStorage.deleteItem('lpss');

    const route = {
      href: getLinkPath(course),
      as: getLink(course)
    };
    pushRoute(route.href, route.as);
  };

  const courseInfo = {
    title: name,
    langLabel: languageDisplay,
    topicName: topicGroups[0]?.name,
    educatorName: `${author.firstName} ${author.lastName}`,
    educatorLink: `/@${author.username}`,
    thumbnail: getCourseThumbnail(coverPhotoV1 || coverPhoto),
    detailsLine: label,
    topicColor: topicGroups[0]?.name
      ? getTextColor(topicGroups[0]?.name[0] || '')
      : '#8873FF',
    onClick: handleOnClick
  };

  return courseInfo;
}

export function formatPractice(practice, goalInfo) {
  const { name, totalQuestionCount, childrenCount } = practice;
  const concepts = nFormatter(totalQuestionCount);
  const questions = nFormatter(childrenCount);
  const description = `${concepts} concepts  • ${questions} questions`;
  const practiceInfo = {
    title: name,
    description,
    href: `/goal/${goalInfo?.slug}/${goalInfo?.uid}/practice/topics/${practice?.uid}`
  };
  return practiceInfo;
}

export const Preview = ({
  url,
  color,
  handlePlayCTA,
  isLive,
  isMobile,
  className
}) => {
  return (
    <PreviewContainer className={className}>
      <PreviewWrapper onClick={handlePlayCTA}>
        {isLive && (
          <LiveClassTag variant="solid" color="var(--color-i-red)" size="small">
            Live
          </LiveClassTag>
        )}
        <ImageWrapper>
          <EduImage
            src={url}
            alt="edu-image"
            priority
            height={261}
            width={464}
          />
        </ImageWrapper>
        <PlayIconButton size="large" $buttonColor={color} $isLive={isLive}>
          <Play height={isMobile ? 12 : 17} width={isMobile ? 12 : 17} />
        </PlayIconButton>
      </PreviewWrapper>
    </PreviewContainer>
  );
};

export const getDuration = (time, separator = ':') => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time - hours * 3600) / 60);
  const seconds = Math.floor(time - hours * 3600 - minutes * 60);

  let formatedTime = '';
  let addSeparator = false;

  if (hours > 0) {
    formatedTime += `${hours}`;
    addSeparator = true;
  }

  if (minutes > 0 || minutes === 0) {
    if (addSeparator) {
      formatedTime += `${separator}`;
    }

    formatedTime += minutes > 9 ? `${minutes}` : `0${minutes}`;
    addSeparator = true;
  }

  if (seconds > 0) {
    if (addSeparator) {
      formatedTime += `${separator}`;
    }

    formatedTime += seconds > 9 ? `${seconds}` : `0${seconds}`;
  }

  if (!formatedTime) {
    return `${time}s`;
  }

  return formatedTime;
};
